import { useMemo, useSyncExternalStore } from 'react';
import { AssetState, EditorStarterAsset } from '../assets/assets';
import { cacheAssetLocally, deleteCachedAsset, getObject } from './indexeddb';
import {
  isConcurrentDownloadEnabled,
  isPrefetchEnabled,
  setConcurrentDownloadEnabled,
  setPrefetchEnabled,
} from '@/shared/lib/editor-runtime-options';

export {
  isConcurrentDownloadEnabled,
  isPrefetchEnabled,
  setConcurrentDownloadEnabled,
  setPrefetchEnabled,
};

/**
 * Web Audio API로 오디오를 사전 디코딩하여 브라우저 캐시에 올림.
 * Remotion의 Html5Audio가 재생 시 동일 blob 데이터를 디코딩할 때
 * 브라우저가 캐시된 결과를 재사용하여 첫 재생 지연을 방지함.
 */
let sharedAudioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
    sharedAudioContext = new AudioContext();
  }
  return sharedAudioContext;
};

const preDecodeAudio = async (blob: Blob): Promise<void> => {
  const arrayBuffer = await blob.arrayBuffer();
  const audioContext = getAudioContext();
  await audioContext.decodeAudioData(arrayBuffer);
};

/**
 * 브라우저 오디오 파이프라인(DAC, 오디오 스레드)을 사전 활성화.
 * 짧은 무음 버퍼를 재생하여 첫 번째 실제 오디오 재생 시
 * 초기화 지연(50-200ms)을 제거함.
 */
let audioWarmupDone = false;

export const warmUpAudioPipeline = (): void => {
  if (audioWarmupDone) return;
  audioWarmupDone = true;

  try {
    const ctx = getAudioContext();
    // 1 샘플짜리 무음 버퍼 생성 후 즉시 재생
    const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
  } catch {
    // AudioContext 생성 실패 시 무시 (SSR 등)
  }
};

const opens: Record<string, Promise<string | null>> = {};
let localUrls: Record<string, string> = {};
let localUrlCacheKeys: Record<string, string> = {};
let failedAssetIds: Record<string, boolean> = {};
let downloadProgress: Record<string, number | null> = {};

let subscribeToChangeCallbacks: (() => void)[] = [];
let subscribeToDownloadProgressCallbacks: (() => void)[] = [];

const lastCheckIfAssetUrlIsStillValid: Record<string, number> = {};
const pendingCheckIfAssetUrlIsStillValid: Record<
  string,
  Promise<boolean> | undefined
> = {};
let assetRuntimeGeneration = 0;

class AssetRuntimeCanceledError extends Error {
  constructor() {
    super('Asset runtime was cleared');
    this.name = 'AssetRuntimeCanceledError';
  }
}

const createAssetRuntimeCanceledError = () => new AssetRuntimeCanceledError();

const isAssetRuntimeCanceledError = (
  err: unknown,
): err is AssetRuntimeCanceledError => {
  return err instanceof AssetRuntimeCanceledError;
};

const isRuntimeActive = (generation: number) => {
  return generation === assetRuntimeGeneration;
};

const assertRuntimeActive = (generation: number) => {
  if (!isRuntimeActive(generation)) {
    throw createAssetRuntimeCanceledError();
  }
};

const isBlobUrlStillValid = async (url: string) => {
  try {
    const res = await fetch(url);
    return res.ok;
  } catch {
    return false;
  }
};

const reloadBlobUrl = async (localUrl: string, asset: EditorStarterAsset) => {
  const cacheKey = getRemoteAssetCacheKey(asset);
  delete localUrls[asset.id];
  delete localUrlCacheKeys[asset.id];
  delete opens[cacheKey];
  subscribeToChangeCallbacks.forEach((fn) => fn());
  URL.revokeObjectURL(localUrl);
  await loadToBlobUrlOnce(asset);
};

const getAssetSignature = (asset: EditorStarterAsset): string => {
  const duration =
    'durationInSeconds' in asset ? String(asset.durationInSeconds ?? '') : '';
  return [asset.remoteUrl ?? '', asset.size ?? '', duration].join('|');
};

const REMOTE_ASSET_CACHE_SCHEMA_VERSION = 'v2';

export const getRemoteAssetCacheKey = (asset: EditorStarterAsset): string => {
  if (!asset.remoteUrl) return asset.id;
  return `${REMOTE_ASSET_CACHE_SCHEMA_VERSION}:${asset.id}:${getAssetSignature(asset)}`;
};

export const shouldPersistRemoteAssetCache = (
  asset: EditorStarterAsset,
): boolean => {
  return !(asset.remoteUrl && asset.type === 'audio');
};

const getRemoteFetchCacheMode = (
  asset: EditorStarterAsset,
): RequestCache | undefined => {
  if (asset.remoteUrl && asset.type === 'audio') {
    return 'no-store';
  }

  return undefined;
};

export const checkIfLocalUrlIsStillValid = async (
  localUrl: string,
  asset: EditorStarterAsset,
): Promise<boolean> => {
  if (!(await isBlobUrlStillValid(localUrl))) {
    await reloadBlobUrl(localUrl, asset);
    return false; // URL이 유효하지 않아서 재생성됨
  }
  return true; // URL이 유효함
};

// browser들은 시간이 지나면 blob URL을 제거하므로, 주기적으로 유효성을 확인해야 함
// https://github.com/remotion-dev/editor-starter/issues/398
export const periodicallyCheckIfLocalUrlIsStillValid = async (
  localUrl: string,
  asset: EditorStarterAsset,
) => {
  if (pendingCheckIfAssetUrlIsStillValid[asset.id]) {
    return;
  }

  // 20초마다만 확인
  if (
    (lastCheckIfAssetUrlIsStillValid[asset.id] ?? 0) + 20 * 1000 >=
    Date.now()
  ) {
    return;
  }

  pendingCheckIfAssetUrlIsStillValid[asset.id] = checkIfLocalUrlIsStillValid(
    localUrl,
    asset,
  );

  await pendingCheckIfAssetUrlIsStillValid[asset.id];
  pendingCheckIfAssetUrlIsStillValid[asset.id] = undefined;
  lastCheckIfAssetUrlIsStillValid[asset.id] = Date.now();
};

const loadFromAssetToLocalUrl = async (
  asset: EditorStarterAsset,
  generation: number,
) => {
  const cacheKey = getRemoteAssetCacheKey(asset);
  const blob = (await getObject({ key: cacheKey })) as Blob | undefined;
  if (!isRuntimeActive(generation)) {
    return null;
  }
  if (!blob) {
    return null;
  }

  const url = URL.createObjectURL(blob);

  const isValid = await isBlobUrlStillValid(url);
  if (!isRuntimeActive(generation)) {
    URL.revokeObjectURL(url);
    return null;
  }
  if (!isValid) {
    await deleteCachedAsset({ assetId: cacheKey });
    URL.revokeObjectURL(url);
    return null;
  }

  localUrls = { ...localUrls, [asset.id]: url };
  localUrlCacheKeys = { ...localUrlCacheKeys, [asset.id]: cacheKey };
  subscribeToChangeCallbacks.forEach((fn) => fn());
  return url;
};

export const setLocalUrl = (assetId: string, url: string) => {
  localUrls = { ...localUrls, [assetId]: url };
  localUrlCacheKeys = { ...localUrlCacheKeys, [assetId]: assetId };
  subscribeToChangeCallbacks.forEach((fn) => fn());
};

export const loadToBlobUrlOnce = (asset: EditorStarterAsset) => {
  const cacheKey = getRemoteAssetCacheKey(asset);
  const generation = assetRuntimeGeneration;

  if (opens[cacheKey] !== undefined) {
    return opens[cacheKey];
  }

  if (localUrls[asset.id] && localUrlCacheKeys[asset.id] === cacheKey) {
    return Promise.resolve();
  }

  if (localUrls[asset.id] && localUrlCacheKeys[asset.id] !== cacheKey) {
    const staleLocalUrl = localUrls[asset.id];
    if (staleLocalUrl) {
      URL.revokeObjectURL(staleLocalUrl);
    }
    const { [asset.id]: _url, ...restUrls } = localUrls;
    const { [asset.id]: _cacheKey, ...restCacheKeys } = localUrlCacheKeys;
    localUrls = restUrls;
    localUrlCacheKeys = restCacheKeys;
  }

  if (!asset.remoteUrl && asset.type === 'caption') {
    return Promise.resolve();
  }

  opens[cacheKey] = loadFromAssetToLocalUrl(asset, generation);
  return opens[cacheKey];
};

export const getLocalUrls = () => {
  return localUrls;
};

export const useLocalUrls = () => {
  return useSyncExternalStore(
    (cb) => {
      subscribeToChangeCallbacks.push(cb);

      return () => {
        subscribeToChangeCallbacks = subscribeToChangeCallbacks.filter(
          (fn) => fn !== cb,
        );
      };
    },
    getLocalUrls,
    getLocalUrls,
  );
};

export const useDownloadProgress = (assetId: string) => {
  return useSyncExternalStore(
    (cb) => {
      subscribeToDownloadProgressCallbacks.push(cb);

      return () => {
        subscribeToDownloadProgressCallbacks =
          subscribeToDownloadProgressCallbacks.filter((fn) => fn !== cb);
      };
    },
    () => downloadProgress[assetId],
  );
};

const downloadProgressesOnServer: Record<string, number | null> = {};

const getDownloadProgressesOnServer = () => {
  return downloadProgressesOnServer;
};

export const useDownloadProgresses = () => {
  return useSyncExternalStore(
    (cb) => {
      subscribeToDownloadProgressCallbacks.push(cb);

      return () => {
        subscribeToDownloadProgressCallbacks =
          subscribeToDownloadProgressCallbacks.filter((fn) => fn !== cb);
      };
    },
    () => downloadProgress,
    getDownloadProgressesOnServer,
  );
};

const downloads: Record<string, Promise<void>> = {};

/**
 * 동시 다운로드 제한 큐.
 * 비디오 다운로드가 대역폭을 모두 점유하면 스트리밍 재생이 끊기므로
 * 동시 다운로드를 MAX_CONCURRENT_DOWNLOADS개로 제한.
 * 오디오/캡션 등 작은 에셋은 큐를 거치지 않고 즉시 다운로드.
 */
const MAX_CONCURRENT_DOWNLOADS = 1;
let activeDownloads = 0;
type DownloadQueueEntry = {
  generation: number;
  resolve: () => void;
  reject: (error: Error) => void;
};
const downloadQueue: DownloadQueueEntry[] = [];

/**
 * 재생 중 다운로드 일시정지.
 * 재생 시 setDownloadPaused(true) → 진행 중인 fetch를 abort → CDN 연결 해제.
 * 재생 완료/카드 비활성화 시 자동 재개 (이어받기).
 * concurrentDownloadEnabled가 true이면 pause를 무시하여 재생 중에도 다운로드 계속.
 */
let downloadPaused = false;
let pauseResolvers: (() => void)[] = [];
let activeDownloadController: AbortController | null = null;
const playbackBoundaryDownloadPriorityTokens = new Set<symbol>();

const isBackgroundDownloadPaused = () =>
  downloadPaused || playbackBoundaryDownloadPriorityTokens.size > 0;

const resumePausedDownloadsIfAllowed = () => {
  if (isBackgroundDownloadPaused()) return;
  const resolvers = pauseResolvers;
  pauseResolvers = [];
  resolvers.forEach((resolve) => resolve());
};

/**
 * 다음 아바타 세그먼트의 첫 프레임을 준비하는 짧은 구간에만 원본 백그라운드
 * 다운로드를 양보시킨다. concurrentDownload 전역 설정과 generic media 정책은
 * 바꾸지 않으며, 반환된 release를 호출하면 기존 Range 재개 루프가 이어받는다.
 */
export const acquirePlaybackBoundaryDownloadPriority = (
  label: string,
): (() => void) => {
  const token = Symbol(label);
  playbackBoundaryDownloadPriorityTokens.add(token);
  activeDownloadController?.abort();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    playbackBoundaryDownloadPriorityTokens.delete(token);
    resumePausedDownloadsIfAllowed();
  };
};

export const setDownloadPaused = (paused: boolean) => {
  // 동시 다운로드 모드면 pause 요청 무시
  if (paused && isConcurrentDownloadEnabled()) return;
  downloadPaused = paused;
  if (paused) {
    // 진행 중인 다운로드 fetch를 abort하여 CDN 연결 해제
    activeDownloadController?.abort();
  } else {
    resumePausedDownloadsIfAllowed();
  }
};

const waitIfPaused = async (generation: number): Promise<void> => {
  if (!isBackgroundDownloadPaused()) return Promise.resolve();
  await new Promise<void>((resolve) => {
    pauseResolvers.push(resolve);
  });
  assertRuntimeActive(generation);
};

const waitForSlot = (generation: number): Promise<void> => {
  assertRuntimeActive(generation);
  if (activeDownloads < MAX_CONCURRENT_DOWNLOADS) {
    activeDownloads++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    downloadQueue.push({
      generation,
      resolve: () => {
        assertRuntimeActive(generation);
        activeDownloads++;
        resolve();
      },
      reject,
    });
  });
};

const releaseSlot = (generation: number) => {
  if (!isRuntimeActive(generation)) {
    return;
  }
  if (activeDownloads <= 0) {
    return;
  }

  activeDownloads--;
  const next = downloadQueue.shift();
  if (!next) return;

  if (!isRuntimeActive(next.generation)) {
    next.reject(createAssetRuntimeCanceledError());
    return;
  }

  try {
    next.resolve();
  } catch (err) {
    next.reject(
      err instanceof Error ? err : new Error('Failed to resume download queue'),
    );
  }
};

const restoreLocalUrlFromCache = async (
  asset: EditorStarterAsset,
  generation: number,
): Promise<boolean> => {
  const cacheKey = getRemoteAssetCacheKey(asset);
  const cachedBlob = (await getObject({ key: cacheKey })) as Blob | undefined;
  assertRuntimeActive(generation);
  if (!cachedBlob) {
    return false;
  }

  const url = URL.createObjectURL(cachedBlob);
  const isValid = await isBlobUrlStillValid(url);
  if (!isRuntimeActive(generation)) {
    URL.revokeObjectURL(url);
    throw createAssetRuntimeCanceledError();
  }
  if (!isValid) {
    await deleteCachedAsset({ assetId: cacheKey });
    URL.revokeObjectURL(url);
    return false;
  }

  localUrls = { ...localUrls, [asset.id]: url };
  localUrlCacheKeys = { ...localUrlCacheKeys, [asset.id]: cacheKey };
  downloadProgress = { ...downloadProgress, [asset.id]: cachedBlob.size };
  subscribeToChangeCallbacks.forEach((fn) => fn());
  subscribeToDownloadProgressCallbacks.forEach((fn) => fn());
  return true;
};

const downloadToCache = async (
  asset: EditorStarterAsset,
  assetStatus: AssetState | null,
  generation: number,
): Promise<void> => {
  assertRuntimeActive(generation);
  if (!asset.remoteUrl) {
    throw new Error('Asset has no remote URL');
  }

  if (assetStatus && assetStatus.type !== 'uploaded') {
    throw new Error('Cannot download asset: upload not complete');
  }

  const chunks: Uint8Array[] = [];
  let loadedBytes = 0;

  // 재생 중 abort → 재개 시 이어받기 루프
  while (true) {
    // 재생 중이면 다운로드 시작 전에 대기
    await waitIfPaused(generation);
    assertRuntimeActive(generation);

    const controller = new AbortController();
    activeDownloadController = controller;

    try {
      const headers: Record<string, string> = {};
      if (loadedBytes > 0) {
        headers['Range'] = `bytes=${loadedBytes}-`;
      }

      const response = await fetch(asset.remoteUrl, {
        signal: controller.signal,
        headers,
        cache: getRemoteFetchCacheMode(asset),
      } as RequestInit);

      if (!response.ok && response.status !== 206) {
        throw new Error(
          `Cannot download asset: HTTP ${response.status} ${response.statusText}`,
        );
      }

      if (!response.body) {
        const fullBlob = await response.blob();
        const arr = new Uint8Array(await fullBlob.arrayBuffer());
        chunks.push(arr);
        loadedBytes += arr.length;
        break;
      }

      const reader = response.body.getReader();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          assertRuntimeActive(generation);
          chunks.push(value);
          loadedBytes += value.length;
          downloadProgress = { ...downloadProgress, [asset.id]: loadedBytes };
          subscribeToDownloadProgressCallbacks.forEach((fn) => fn());
        }
      }

      // 정상 완료
      break;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        if (!isRuntimeActive(generation)) {
          throw createAssetRuntimeCanceledError();
        }
        // 재생 시작으로 인한 abort → 재개 대기
        activeDownloadController = null;
        continue;
      }
      // 기타 에러는 상위로 전파
      activeDownloadController = null;
      throw err;
    } finally {
      activeDownloadController = null;
    }
  }

  assertRuntimeActive(generation);
  const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
  const uint8Array = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    uint8Array.set(chunk, offset);
    offset += chunk.length;
  }

  const blob = new Blob([uint8Array], { type: asset.mimeType });
  const cacheKey = getRemoteAssetCacheKey(asset);
  assertRuntimeActive(generation);

  // Blob URL을 즉시 등록하여 IDB에서 다시 읽는 단계를 건너뜀
  const url = URL.createObjectURL(blob);
  localUrls = { ...localUrls, [asset.id]: url };
  localUrlCacheKeys = { ...localUrlCacheKeys, [asset.id]: cacheKey };
  downloadProgress = { ...downloadProgress, [asset.id]: blob.size };
  subscribeToChangeCallbacks.forEach((fn) => fn());

  // 오디오 에셋은 Web Audio API로 사전 디코딩하여 첫 재생 지연 방지
  // 브라우저가 디코딩 결과를 캐시하므로, Remotion의 Html5Audio가 재생할 때 즉시 시작됨
  if (asset.type === 'audio') {
    preDecodeAudio(blob).catch((err) => {
      console.warn('[downloadToCache] Audio pre-decode failed:', asset.id, err);
    });
  }

  // IDB에 백그라운드로 캐시 (다음 세션을 위해)
  // 원격 오디오는 같은 contentId/S3 key가 교체될 수 있어 장기 캐시하지 않는다.
  if (isRuntimeActive(generation) && shouldPersistRemoteAssetCache(asset)) {
    cacheAssetLocally({ assetId: cacheKey, value: blob }).catch((err) => {
      console.error('[downloadToCache] IDB 저장 실패:', asset.id, err);
    });
  }
  subscribeToDownloadProgressCallbacks.forEach((fn) => fn());
};

export const downloadToCacheOnce = (
  asset: EditorStarterAsset,
  assetStatus: AssetState | null,
): Promise<void> => {
  const cacheKey = getRemoteAssetCacheKey(asset);
  const generation = assetRuntimeGeneration;

  if (localUrls[asset.id] && localUrlCacheKeys[asset.id] === cacheKey) {
    return Promise.resolve();
  }

  if (!asset.remoteUrl && asset.type === 'caption') {
    return Promise.resolve();
  }

  if (downloads[cacheKey] !== undefined) {
    return downloads[cacheKey] as Promise<void>;
  }

  // 최근 실패한 에셋은 5초 내 재시도 스킵 (무한 재시도 방지)
  if (failedAssetIds[asset.id]) {
    return Promise.resolve();
  }

  // 모든 에셋을 큐를 통해 순차 다운로드 (CDN 503 방지)
  const downloadPromise = (async () => {
    const restored = shouldPersistRemoteAssetCache(asset)
      ? await restoreLocalUrlFromCache(asset, generation)
      : false;
    if (restored) {
      return;
    }

    await waitForSlot(generation);
    try {
      await downloadToCache(asset, assetStatus, generation);
    } finally {
      releaseSlot(generation);
    }
  })().catch((err) => {
    if (downloads[cacheKey] === downloadPromise) {
      delete downloads[cacheKey];
    }
    if (isAssetRuntimeCanceledError(err)) {
      return;
    }
    failedAssetIds = { ...failedAssetIds, [asset.id]: true };
    // 5초 후 failedAssetIds에서 제거하여 재시도 가능하게
    setTimeout(() => {
      const { [asset.id]: _, ...rest } = failedAssetIds;
      failedAssetIds = rest;
      subscribeToChangeCallbacks.forEach((fn) => fn());
    }, 5000);
    subscribeToChangeCallbacks.forEach((fn) => fn());
    console.warn(`Download failed asset=${asset.id}:`, err?.message);
  });
  downloads[cacheKey] = downloadPromise;
  return downloadPromise;
};

const getFailedAssetIds = () => failedAssetIds;

export const useFailedAssetIds = () => {
  return useSyncExternalStore(
    (cb) => {
      subscribeToChangeCallbacks.push(cb);
      return () => {
        subscribeToChangeCallbacks = subscribeToChangeCallbacks.filter(
          (fn) => fn !== cb,
        );
      };
    },
    getFailedAssetIds,
    getFailedAssetIds,
  );
};

export const clearAllBlobUrls = () => {
  assetRuntimeGeneration++;
  const urls = Object.values(localUrls);
  urls.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  localUrls = {};
  localUrlCacheKeys = {};
  Object.keys(opens).forEach((key) => delete opens[key]);
  Object.keys(downloads).forEach((key) => delete downloads[key]);
  failedAssetIds = {};
  downloadProgress = {};
  activeDownloads = 0;
  const queuedDownloads = downloadQueue.splice(0);
  queuedDownloads.forEach((entry) => {
    entry.reject(createAssetRuntimeCanceledError());
  });
  downloadPaused = false;
  playbackBoundaryDownloadPriorityTokens.clear();
  const waitingForPause = pauseResolvers;
  pauseResolvers = [];
  waitingForPause.forEach((resolve) => resolve());
  const controller = activeDownloadController;
  activeDownloadController = null;
  controller?.abort();
  subscribeToChangeCallbacks.forEach((fn) => fn());
  subscribeToDownloadProgressCallbacks.forEach((fn) => fn());
};

/**
 * 모든 에셋이 로컬에 캐시되었는지 확인하는 훅
 * 다운로드 대상: remoteUrl이 있는 에셋만 (downloadToCacheOnce가 remoteUrl 필수)
 * 제외: caption without remoteUrl, 업로드 중(remoteUrl+staticPath 없음), staticPath만 있는 에셋
 * 실패한 에셋도 "완료"로 간주하여 isComplete 판정에서 무한 로딩 방지
 */
export const usePrefetchStatus = (
  assets: Record<string, EditorStarterAsset>,
) => {
  const currentLocalUrls = useLocalUrls();
  const currentFailedAssets = useFailedAssetIds();

  return useMemo(() => {
    const assetIds = Object.keys(assets).filter((id) => {
      const asset = assets[id];
      if (!asset) return false;
      // remoteUrl이 있는 에셋만 다운로드 대상 (staticPath만 있는 에셋은 public 폴더에서 직접 로드)
      if (!asset.remoteUrl) return false;
      return true;
    });

    const totalAssets = assetIds.length;
    if (totalAssets === 0) {
      return {
        isComplete: true,
        percentage: 100,
        totalAssets: 0,
        cachedAssets: 0,
        hasFailedAssets: false,
      };
    }

    const cachedAssets = assetIds.filter((id) => currentLocalUrls[id]).length;
    const failedCount = assetIds.filter((id) => currentFailedAssets[id]).length;
    const resolvedAssets = cachedAssets + failedCount;
    const percentage = Math.round((resolvedAssets / totalAssets) * 100);

    return {
      isComplete: resolvedAssets >= totalAssets,
      percentage,
      totalAssets,
      cachedAssets,
      hasFailedAssets: failedCount > 0,
    };
  }, [assets, currentLocalUrls, currentFailedAssets]);
};
